import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Briefcase, Clock, Filter, Linkedin } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SEO, { SEO_CONFIGS } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedSector, setSelectedSector] = useState('all');
  const [loading, setLoading] = useState(true);

  const sectors = ['All', 'Manufacturing', 'Engineering', 'Automation', 'Fashion and Apparel', 'Energy', 'Emerging Technology'];

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedSector === 'all') {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs(jobs.filter(job => job.sector === selectedSector));
    }
  }, [selectedSector, jobs]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
      setFilteredJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const shareOnLinkedIn = (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    const jobUrl = `https://vocavi.in/jobs/${job.id}`;
    const title = encodeURIComponent(`${job.title} - ${job.location}`);
    const summary = encodeURIComponent(`${job.title} opportunity at ${job.sector} sector. ${job.experience} experience required. Apply now through Vocavi Partners!`);
    const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(jobUrl)}&title=${title}&summary=${summary}&source=Vocavi%20Partners`;
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
  };

  return (
    <div>
      <SEO {...SEO_CONFIGS.jobs} />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-primary text-white" data-testid="jobs-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-6xl font-bold mb-6"
          >
            Career <span className="gold-text">Opportunities</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-300 max-w-3xl"
          >
            Explore executive and leadership positions across industries
          </motion.p>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="section-spacing bg-white" data-testid="jobs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="mb-8 flex items-center gap-2 overflow-x-auto pb-4" data-testid="job-filters">
            <Filter size={20} className="text-muted-foreground flex-shrink-0" />
            {sectors.map((sector) => (
              <button
                key={sector}
                data-testid={`filter-${sector.toLowerCase().replace(' ', '-')}`}
                onClick={() => setSelectedSector(sector === 'All' ? 'all' : sector)}
                className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                  (selectedSector === 'all' && sector === 'All') || selectedSector === sector
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {sector}
              </button>
            ))}
          </div>

          {/* Jobs List */}
          {loading ? (
            <div className="text-center py-12" data-testid="jobs-loading">
              <p className="text-muted-foreground">Loading opportunities...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12" data-testid="jobs-empty">
              <Briefcase size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No opportunities found for this sector.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  data-testid={`job-item-${job.id}`}
                  className="job-card block p-6 hover:shadow-md transition-all border border-slate-200 bg-white"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <Link to={`/jobs/${job.id}`} className="flex-1">
                          <h3 className="text-xl font-bold text-primary hover:text-primary/80 transition-colors">{job.title}</h3>
                        </Link>
                        <button
                          onClick={(e) => shareOnLinkedIn(e, job)}
                          data-testid={`share-linkedin-${job.id}`}
                          className="ml-4 p-2 text-[#0077B5] hover:bg-[#0077B5]/10 rounded-full transition-colors"
                          title="Share on LinkedIn"
                        >
                          <Linkedin size={20} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center">
                          <MapPin size={16} className="mr-1" />
                          {job.location}
                        </span>
                        <span className="flex items-center">
                          <Briefcase size={16} className="mr-1" />
                          {job.sector}
                        </span>
                        <span className="flex items-center">
                          <Clock size={16} className="mr-1" />
                          {job.experience}
                        </span>
                      </div>
                      <p className="text-muted-foreground line-clamp-2">{job.description}</p>
                    </div>
                    <div className="mt-4 lg:mt-0 lg:ml-8">
                      <Link 
                        to={`/jobs/${job.id}`}
                        className="inline-flex items-center text-secondary font-semibold whitespace-nowrap hover:text-secondary/80 transition-colors"
                      >
                        View Details <ArrowRight className="ml-2" size={18} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-muted" data-testid="jobs-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4">
            Don't See the Right Opportunity?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Submit your resume and we'll connect with you when relevant positions become available.
          </p>
          <Link
            to="/contact"
            data-testid="jobs-cta-submit-resume"
            className="bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all inline-block"
          >
            Submit Your Resume
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Jobs;